import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Menu, MenuItem, Paper, Tooltip, Typography } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import $ from "jquery";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.min.css";
import "daterangepicker/daterangepicker.css";
import "daterangepicker";
import "select2/dist/css/select2.min.css";
import "select2/dist/js/select2.full.min.js";
import { auditAPI, masterDataAPI, userAPI } from "../services/api";
import { getUser, getUserPrivilegeDetails, getUserPrivileges, getUserRoles } from "../utils/auth";
import "./ServiceRequestActivityLog.css";

const DEFAULT_PAGE_SIZE = 5;

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hours24 = d.getHours();
  const hh12 = hours24 % 12 || 12;
  const hh = String(hh12).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const tt = hours24 >= 12 ? "PM" : "AM";
  return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss} ${tt}`;
};

const formatOnlyDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "-"
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const getStatusColor = (status) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  if (normalizedStatus === "open") return "#1976d2";
  if (normalizedStatus === "rejected") return "#ff0000";
  if (normalizedStatus === "pending" || normalizedStatus.includes("pending")) return "#ff9800";
  if (
    normalizedStatus === "approved" ||
    normalizedStatus === "reopen" ||
    normalizedStatus === "closed" ||
    normalizedStatus === "wip"
  ) {
    return "#4caf50";
  }
  return "#000000";
};

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatHeaderDate = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const getCurrentFinancialYearRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const fyStartYear = month < 3 ? year - 1 : year;
  const fyEndYear = month < 3 ? year : year + 1;
  const start = `${fyStartYear}-04-01`;
  const end = `${fyEndYear}-03-31`;
  const fmt = (y, m, d) => `${String(d).padStart(2, "0")}-${String(m).padStart(2, "0")}-${y}`;
  return {
    start,
    end,
    displayText: `${fmt(fyStartYear, 4, 1)} - ${fmt(fyEndYear, 3, 31)}`,
  };
};

const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

const getQuarterStart = (date) => {
  const month = date.getMonth();
  const quarter = Math.floor(month / 3);
  return new Date(date.getFullYear(), quarter * 3, 1);
};

const getFinancialYearStart = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return month < 3 ? new Date(year - 1, 3, 1) : new Date(year, 3, 1);
};

const normalizeExportRows = (rows, marketMap) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    const activityOn = row.activityOn ?? row.ActivityOn ?? row.changedOn ?? row.ChangedOn;
    const changedBy =
      row.fullName ??
      row.FullName ??
      row.displayName ??
      row.DisplayName ??
      row.changedBy ??
      row.ChangedBy ??
      "-";
    const srCode = row.srCode ?? row.SRCode ?? "-";
    const childSrCode = row.nextApproverChildSRNumber ?? row.NextApproverChildSRNumber ?? "";
    const displaySrCode = childSrCode || srCode;
    const oldStatus = row.oldStatusCode ?? row.OldStatusCode ?? row.OldStatuscode ?? "-";
    const newStatus = row.newStatusCode ?? row.NewStatusCode ?? row.NewStatuscode ?? "-";
    const comments = row.comments ?? row.Comments ?? "";
    const userName =
      row.fullName ??
      row.FullName ??
      row.displayName ??
      row.DisplayName ??
      row.userName ??
      row.UserName ??
      row.changedBy ??
      row.ChangedBy ??
      "-";
    const marketCode = row.marketCode ?? row.MarketCode ?? "-";
    const marketName = marketMap?.[marketCode] || marketCode;

    return {
      ActivityOn: formatDateTime(activityOn),
      ChangedBy: changedBy,
      SRNumber: displaySrCode,
      ParentSRCode: srCode,
      FromStatus: oldStatus,
      ToStatus: newStatus,
      Comments: comments,
      User: userName,
      EmployeeCode: row.employeeCode ?? row.EmployeeCode ?? "-",
      Market: marketName,
      CreatedDate: formatOnlyDate(row.userCreatedOn ?? row.UserCreatedOn ?? row.createdOn ?? row.CreatedOn),
    };
  });

const ServiceRequestActivityLog = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => toIsoDate(new Date()));
  const [marketMap, setMarketMap] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [filterExpanded, setFilterExpanded] = useState(true);
  const defaultFY = getCurrentFinancialYearRange();
  const [adminFilters, setAdminFilters] = useState({
    fromDate: defaultFY.start,
    toDate: defaultFY.end,
    userIdCode: "",
  });
  const [appliedAdminFilters, setAppliedAdminFilters] = useState({
    fromDate: defaultFY.start,
    toDate: defaultFY.end,
    userIdCode: "",
  });
  const containerRef = useRef(null);
  const adminDateRangePickerRef = useRef(null);
  const adminUserSelectRef = useRef(null);

  useEffect(() => {
    const user = getUser();
    const roles = getUserRoles();
    const privileges = getUserPrivileges();
    const privilegeDetails = getUserPrivilegeDetails();
    const userTypeCode = String(user?.UserTypeCode || user?.userTypeCode || "").trim().toUpperCase();
    const userTypeName = String(
      user?.UserTypeName || user?.userTypeName || user?.Designation || user?.designation || ""
    )
      .trim()
      .toUpperCase();
    const roleCodes = (Array.isArray(roles) ? roles : [])
      .map((role) =>
        String(role?.RoleCode || role?.roleCode || role?.RoleName || role?.roleName || role || "")
          .trim()
          .toUpperCase()
      )
      .filter(Boolean);
    const isDosAdmin =
      userTypeCode === "UT0000024" || userTypeName === "DOS ADMIN";
    const hasAdminType =
      !isDosAdmin &&
      (userTypeCode === "ADMIN" ||
        userTypeCode === "SYSADMIN001" ||
        userTypeCode === "UT0000017" ||
        userTypeName === "ADMIN ROLE" ||
        userTypeName.includes("ADMINISTRATOR"));
    const hasAdminRole = roleCodes.includes("ADMIN") || roleCodes.includes("ADMINISTRATOR");
    const hasAdminPrivilege = (Array.isArray(privileges) ? privileges : [])
      .map((x) => String(x || "").trim().toUpperCase())
      .includes("ADMIN_SERVICE_REQUEST_MANAGE");
    const hasAdminPrivilegeDetails = (Array.isArray(privilegeDetails) ? privilegeDetails : []).some((p) => {
      const code = String(p?.MenuCode || p?.menuCode || "").trim().toUpperCase();
      const canView = p?.ViewPermission === true || p?.viewPermission === true;
      return canView && code === "ADMIN_SERVICE_REQUEST_MANAGE";
    });

    setIsAdmin(hasAdminType || hasAdminRole || hasAdminPrivilege || hasAdminPrivilegeDetails);
  }, []);

  const loadPage = useCallback(
    async (nextPage, reset = false, adminFilterOverride = null) => {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError("");
      try {
        const activeAdminFilters = isAdmin ? adminFilterOverride || appliedAdminFilters : null;
        const fromDate = isAdmin ? activeAdminFilters?.fromDate : selectedDate;
        const toDate = isAdmin ? activeAdminFilters?.toDate : selectedDate;
        const response = await auditAPI.getServiceRequestActivityLogs({
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          userIdCode: isAdmin && activeAdminFilters?.userIdCode ? activeAdminFilters.userIdCode : undefined,
          pageNumber: nextPage,
          pageSize: DEFAULT_PAGE_SIZE,
        });

        const payload = response?.data || {};
        const items = Array.isArray(payload.items) ? payload.items : Array.isArray(payload.Items) ? payload.Items : [];
        const totalRecords = Number(payload.totalRecords ?? payload.TotalRecords ?? 0);

        setRows((prev) => (reset ? items : [...prev, ...items]));
        const loaded = (nextPage - 1) * DEFAULT_PAGE_SIZE + items.length;
        setHasMore(loaded < totalRecords);
        setPageNumber(nextPage);
      } catch (e) {
        setError(e?.response?.data?.message || e?.message || "Failed to load activity log.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [selectedDate, isAdmin, appliedAdminFilters]
  );

  useEffect(() => {
    loadPage(1, true);
  }, [loadPage]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await masterDataAPI.getMarkets("", 1, 200);
        const payload = res?.data ?? res?.Data ?? res;
        const list =
          (Array.isArray(payload?.data) ? payload.data : null) ||
          (Array.isArray(payload?.markets) ? payload.markets : null) ||
          (Array.isArray(payload) ? payload : []);
        const mapped = {};
        (Array.isArray(list) ? list : []).forEach((m) => {
          const code = String(m.marketCode || m.MarketCode || m.code || "").trim();
          const name = String(m.marketName || m.MarketName || m.name || "").trim();
          if (code) mapped[code] = name || code;
        });
        if (mounted) setMarketMap(mapped);
      } catch {
        if (mounted) setMarketMap({});
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let mounted = true;
    (async () => {
      try {
        const res = await userAPI.getPaged({ pageNumber: 1, pageSize: 1000, status: "active" });
        const payload = res?.data ?? res?.Data ?? res;
        const list =
          (Array.isArray(payload?.users) ? payload.users : null) ||
          (Array.isArray(payload?.Users) ? payload.Users : null) ||
          (Array.isArray(payload?.data) ? payload.data : null) ||
          (Array.isArray(payload) ? payload : []);
        const normalized = (Array.isArray(list) ? list : [])
          .map((u) => ({
            userIdCode: String(u.userIdCode || u.UserIdCode || "").trim(),
            fullName: String(u.fullName || u.FullName || u.displayName || u.DisplayName || "").trim(),
            userTypeName: String(u.userTypeName || u.UserTypeName || "").trim(),
          }))
          .filter((u) => u.userIdCode);
        if (mounted) setAdminUsers(normalized);
      } catch {
        if (mounted) setAdminUsers([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || !adminDateRangePickerRef.current) return undefined;

    const $input = $(adminDateRangePickerRef.current);
    if (!$input.length) return undefined;

    const initDateRangePicker = () => {
      if ($input.data("daterangepicker")) {
        $input.data("daterangepicker").remove();
        $input.off("apply.daterangepicker");
        $input.off("cancel.daterangepicker");
      }

      const initialStart = moment(adminFilters.fromDate || defaultFY.start, "YYYY-MM-DD");
      const initialEnd = moment(adminFilters.toDate || defaultFY.end, "YYYY-MM-DD");
      const today = moment();
      const yesterday = moment().subtract(1, "days");
      const thisWeekStart = getWeekStart(new Date());
      const lastWeekStart = moment(thisWeekStart).subtract(7, "days");
      const lastWeekEnd = moment(thisWeekStart).subtract(1, "days");
      const thisMonthStart = moment().startOf("month");
      const lastMonthStart = moment().subtract(1, "month").startOf("month");
      const lastMonthEnd = moment().subtract(1, "month").endOf("month");
      const thisQuarterStart = getQuarterStart(new Date());
      const lastQuarterStart = moment(thisQuarterStart).subtract(3, "months");
      const lastQuarterEnd = moment(thisQuarterStart).subtract(1, "days");
      const thisFYStart = getFinancialYearStart(new Date());
      const thisFYEnd = moment(thisFYStart).add(1, "year").subtract(1, "day");
      const lastFYStart = moment(thisFYStart).subtract(1, "year");
      const lastFYEnd = moment(thisFYStart).subtract(1, "day");
      $input.daterangepicker({
        autoUpdateInput: false,
        locale: {
          cancelLabel: "Clear",
          applyLabel: "Apply",
          format: "DD-MM-YYYY",
        },
        opens: "left",
        drops: "down",
        showDropdowns: true,
        startDate: initialStart,
        endDate: initialEnd,
        ranges: {
          Today: [today, today],
          Yesterday: [yesterday, yesterday],
          "This Week": [moment(thisWeekStart), today],
          "Last Week": [lastWeekStart, lastWeekEnd],
          "This Month": [thisMonthStart, today],
          "Last Month": [lastMonthStart, lastMonthEnd],
          "This Quarter": [moment(thisQuarterStart), today],
          "Last Quarter": [lastQuarterStart, lastQuarterEnd],
          "This Financial Year": [moment(thisFYStart), thisFYEnd],
          "Last Financial Year": [lastFYStart, lastFYEnd],
        },
      });

      const setDisplay = (startMoment, endMoment) => {
        $input.val(`${startMoment.format("DD-MM-YYYY")} - ${endMoment.format("DD-MM-YYYY")}`);
      };
      setDisplay(initialStart, initialEnd);

      $input.on("apply.daterangepicker", (ev, picker) => {
        const start = picker.startDate.format("YYYY-MM-DD");
        const end = picker.endDate.format("YYYY-MM-DD");
        setAdminFilters((prev) => ({ ...prev, fromDate: start, toDate: end }));
        setDisplay(picker.startDate, picker.endDate);
      });

      $input.on("cancel.daterangepicker", () => {
        const start = moment(defaultFY.start, "YYYY-MM-DD");
        const end = moment(defaultFY.end, "YYYY-MM-DD");
        setAdminFilters((prev) => ({ ...prev, fromDate: defaultFY.start, toDate: defaultFY.end }));
        const drp = $input.data("daterangepicker");
        if (drp) {
          drp.setStartDate(start);
          drp.setEndDate(end);
        }
        setDisplay(start, end);
      });
    };

    initDateRangePicker();
    return () => {
      if ($input.data("daterangepicker")) {
        $input.off("apply.daterangepicker");
        $input.off("cancel.daterangepicker");
        $input.data("daterangepicker").remove();
      }
    };
  }, [isAdmin, adminFilters.fromDate, adminFilters.toDate, defaultFY.end, defaultFY.start]);

  useEffect(() => {
    if (!isAdmin || !adminUserSelectRef.current) return undefined;
    const $select = $(adminUserSelectRef.current);
    if (!$select.length) return undefined;
    if ($select.hasClass("select2-hidden-accessible")) {
      $select.select2("destroy");
    }

    $select.select2({
      placeholder: "Select User",
      allowClear: true,
      width: "100%",
    });

    $select.off("change.activityUser").on("change.activityUser", function onChange(e) {
      setAdminFilters((prev) => ({ ...prev, userIdCode: e?.target?.value || "" }));
    });

    return () => {
      $select.off("change.activityUser");
      if ($select.hasClass("select2-hidden-accessible")) {
        $select.select2("destroy");
      }
    };
  }, [isAdmin, adminUsers.length]);

  useEffect(() => {
    if (!isAdmin || !adminUserSelectRef.current) return;
    const $select = $(adminUserSelectRef.current);
    if (!$select.length || !$select.hasClass("select2-hidden-accessible")) return;
    const currentVal = $select.val() || "";
    const expectedVal = adminFilters.userIdCode || "";
    if (String(currentVal) !== String(expectedVal)) {
      $select.val(expectedVal).trigger("change.select2");
    }
  }, [isAdmin, adminFilters.userIdCode]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    const onScroll = () => {
      if (loading || loadingMore || !hasMore) return;
      const nearBottom = root.scrollTop + root.clientHeight >= root.scrollHeight - 120;
      if (nearBottom) {
        loadPage(pageNumber + 1, false);
      }
    };

    root.addEventListener("scroll", onScroll);
    return () => root.removeEventListener("scroll", onScroll);
  }, [hasMore, loadPage, loading, loadingMore, pageNumber]);

  // If loaded rows are too few to make the container scrollable,
  // auto-load a few more pages — but cap so DOS Admin / large FY never freezes the UI.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    if (loading || loadingMore || !hasMore) return;
    const MAX_AUTO_FILL_PAGES = 3;
    if (pageNumber >= MAX_AUTO_FILL_PAGES) return;
    const isScrollable = root.scrollHeight > root.clientHeight + 2;
    if (!isScrollable && rows.length > 0) {
      loadPage(pageNumber + 1, false);
    }
  }, [rows.length, hasMore, loading, loadingMore, pageNumber, loadPage]);

  const downloadCsv = () => {
    const exportRows = normalizeExportRows(rows, marketMap);
    if (!exportRows.length) return;

    const headers = Object.keys(exportRows[0]);
    const escapeCell = (val) => {
      const s = val == null ? "" : String(val);
      if (s.includes('"') || s.includes(",") || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const csv = [
      headers.join(","),
      ...exportRows.map((r) => headers.map((h) => escapeCell(r[h])).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileSuffix = isAdmin
      ? `${appliedAdminFilters.fromDate || "from"}_${appliedAdminFilters.toDate || "to"}`
      : selectedDate || "date";
    a.download = `activity_${fileSuffix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcel = async () => {
    const exportRows = normalizeExportRows(rows, marketMap);
    if (!exportRows.length) return;
    setDownloading(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Activity Log");
      const headers = Object.keys(exportRows[0]);
      sheet.addRow(headers);
      exportRows.forEach((r) => sheet.addRow(headers.map((h) => r[h])));
      sheet.getRow(1).font = { bold: true };
      sheet.columns.forEach((column) => {
        let max = 12;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const len = String(cell.value ?? "").length;
          if (len > max) max = len;
        });
        column.width = Math.min(max + 2, 60);
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fileSuffix = isAdmin
        ? `${appliedAdminFilters.fromDate || "from"}_${appliedAdminFilters.toDate || "to"}`
        : selectedDate || "date";
      a.download = `activity_${fileSuffix}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Activity Log
      </Typography>

      {isAdmin && (
        <div className="card-panel mb-3 filter-panel activity-admin-filter-panel">
          <div
            className="section-heading d-flex align-items-center gap-2 mb-3"
            style={{ marginBottom: "14px", cursor: "pointer" }}
            onClick={() => setFilterExpanded((prev) => !prev)}
          >
            <FilterListIcon fontSize="small" className="text-muted" />
            <span>Filter</span>
            {filterExpanded ? (
              <ExpandLessIcon
                fontSize="small"
                className="text-muted"
                style={{ width: "18px", height: "18px", flexShrink: 0, marginLeft: "auto" }}
              />
            ) : (
              <ExpandMoreIcon
                fontSize="small"
                className="text-muted"
                style={{ width: "18px", height: "18px", flexShrink: 0, marginLeft: "auto" }}
              />
            )}
          </div>
          {filterExpanded && (
            <div className="container-fluid">
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-6 col-lg-4">
                  <label className="input-label d-block mb-1 text-start">Select Date Range</label>
                  <div className="input-icon" style={{ position: "relative" }}>
                    <input
                      type="text"
                      ref={adminDateRangePickerRef}
                      className="form-control form-control-light text-start filter-daterangepicker"
                      placeholder="Select date range"
                      readOnly
                      style={{ height: "42px", textAlign: "left" }}
                    />
                    <CalendarTodayIcon className="icon-right" />
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <label className="input-label d-block mb-1 text-start">User</label>
                  <select
                    ref={adminUserSelectRef}
                    className="form-select form-control-light filter-select text-start"
                    style={{ height: "42px", textAlign: "left" }}
                  >
                    <option value="">All Users</option>
                    {adminUsers.map((u) => (
                      <option key={u.userIdCode} value={u.userIdCode}>
                        {u.fullName
                          ? `${u.fullName}${u.userTypeName ? ` (${u.userTypeName})` : ""}`
                          : u.userIdCode}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <label className="input-label d-block mb-1" style={{ visibility: "hidden" }}>
                    Actions
                  </label>
                  <div className="d-flex gap-2 filter-buttons">
                    <button
                      type="button"
                      className="btn btn-filter"
                      onClick={() => {
                        const selectedUserId =
                          String($(adminUserSelectRef.current).val() || adminFilters.userIdCode || "").trim();
                        const nextFilters = { ...adminFilters, userIdCode: selectedUserId };
                        setAdminFilters(nextFilters);
                        setAppliedAdminFilters(nextFilters);
                        loadPage(1, true, nextFilters);
                      }}
                      style={{
                        background: "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                        border: "none",
                        color: "white",
                        fontWeight: 600,
                        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                        borderRadius: "4px",
                        padding: "8px 24px",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.6)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.4)";
                      }}
                      onMouseDown={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                      onMouseUp={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                    >
                      <FilterListIcon fontSize="small" className="me-1" />
                      Filter
                    </button>
                    <button
                      type="button"
                      className="btn btn-download-filter"
                      title="Download"
                      onClick={(e) => setDownloadAnchorEl(e.currentTarget)}
                      disabled={!rows.length || loading || downloading}
                      style={{
                        background: "linear-gradient(135deg, #ff7a18 0%, #ff3d00 100%)",
                        border: "none",
                        color: "white",
                        fontWeight: 600,
                        boxShadow: "0 4px 15px rgba(255, 122, 24, 0.45)",
                        borderRadius: "4px",
                        padding: "8px 12px",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "42px",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(255, 122, 24, 0.6)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(255, 122, 24, 0.45)";
                      }}
                      onMouseDown={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                      onMouseUp={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                    >
                      <FileDownloadIcon fontSize="small" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-reset-filter"
                      title="Reset"
                      onClick={() => {
                        const fy = getCurrentFinancialYearRange();
                        const resetValue = { fromDate: fy.start, toDate: fy.end, userIdCode: "" };
                        setAdminFilters(resetValue);
                        setAppliedAdminFilters(resetValue);
                        if (adminUserSelectRef.current) {
                          $(adminUserSelectRef.current).val("").trigger("change.select2");
                        }
                        loadPage(1, true, resetValue);
                      }}
                      style={{
                        background: "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                        border: "none",
                        color: "white",
                        fontWeight: 600,
                        boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                        borderRadius: "4px",
                        padding: "8px 16px",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(132, 250, 176, 0.6)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(132, 250, 176, 0.4)";
                      }}
                      onMouseDown={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                      onMouseUp={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                    >
                      <RestartAltIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <Paper className="activity-log-card">
        <Box className="activity-log-toolbar">
          {!isAdmin && (
            <>
              <Tooltip title="Previous Day" arrow>
                <Button
                  variant="outlined"
                  size="small"
                  className="activity-icon-btn"
                  aria-label="Previous Day"
                  startIcon={<NavigateBeforeIcon fontSize="small" />}
                  onClick={() => {
                    const d = new Date(`${selectedDate}T00:00:00`);
                    d.setDate(d.getDate() - 1);
                    setSelectedDate(toIsoDate(d));
                  }}
                />
              </Tooltip>
              <Typography className="activity-log-date-label">{formatHeaderDate(selectedDate)}</Typography>
              <Tooltip title="Next Day" arrow>
                <Button
                  variant="outlined"
                  size="small"
                  className="activity-icon-btn"
                  aria-label="Next Day"
                  startIcon={<NavigateNextIcon fontSize="small" />}
                  onClick={() => {
                    const d = new Date(`${selectedDate}T00:00:00`);
                    d.setDate(d.getDate() + 1);
                    setSelectedDate(toIsoDate(d));
                  }}
                />
              </Tooltip>
            </>
          )}
          {!isAdmin && (
            <Tooltip title="Download" arrow>
              {/* span wrapper required: disabled buttons do not fire events MUI Tooltip needs */}
              <span style={{ display: "inline-flex" }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon fontSize="small" />}
                  className="activity-download-btn activity-icon-btn"
                  aria-label="Download"
                  onClick={(e) => setDownloadAnchorEl(e.currentTarget)}
                  disabled={!rows.length || loading || downloading}
                />
              </span>
            </Tooltip>
          )}
          <Menu
            anchorEl={downloadAnchorEl}
            open={Boolean(downloadAnchorEl)}
            onClose={() => setDownloadAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                setDownloadAnchorEl(null);
                downloadCsv();
              }}
            >
              CSV
            </MenuItem>
            <MenuItem
              onClick={async () => {
                setDownloadAnchorEl(null);
                await downloadExcel();
              }}
            >
              Excel
            </MenuItem>
          </Menu>
        </Box>

        <Box ref={containerRef} className="activity-log-timeline-wrap">
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box className="activity-timeline">
              {rows.map((row) => {
                const activityOn = row.activityOn ?? row.ActivityOn ?? row.changedOn ?? row.ChangedOn;
                const changedBy =
                  row.fullName ??
                  row.FullName ??
                  row.displayName ??
                  row.DisplayName ??
                  row.changedBy ??
                  row.ChangedBy ??
                  "-";
                const srCode = row.srCode ?? row.SRCode ?? "-";
                const oldStatus = row.oldStatusCode ?? row.OldStatusCode ?? row.OldStatuscode ?? "-";
                const newStatus = row.newStatusCode ?? row.NewStatusCode ?? row.NewStatuscode ?? "-";
                const comments = row.comments ?? row.Comments ?? "";
                const childSrCode = row.nextApproverChildSRNumber ?? row.NextApproverChildSRNumber ?? "";
                const displaySrCode = childSrCode || srCode;
                const userName =
                  row.fullName ??
                  row.FullName ??
                  row.displayName ??
                  row.DisplayName ??
                  row.userName ??
                  row.UserName ??
                  row.changedBy ??
                  row.ChangedBy ??
                  "-";
                const marketCode = row.marketCode ?? row.MarketCode ?? "-";
                const marketName = marketMap[marketCode] || marketCode;

                return (
                  <Box className="timeline-item" key={row.historyId || row.HistoryId}>
                    <Box className="timeline-time">{formatDateTime(activityOn)}</Box>
                    <Box className="timeline-track">
                      <span className="timeline-dot" />
                    </Box>
                    <Box className="timeline-content">
                      <Typography className="timeline-text">
                        <span className="timeline-user">{changedBy}</span>{" "}
                        changed{" "}
                        <a
                          href="#"
                          className="timeline-link"
                          onClick={(e) => {
                            e.preventDefault();
                            if (!srCode || srCode === "-") return;
                            const parentEncoded = encodeURIComponent(srCode);
                            const childEncoded = childSrCode ? encodeURIComponent(childSrCode) : "";
                            const query = childEncoded ? `?childSrCode=${childEncoded}` : "";
                            navigate(`/complaints/${parentEncoded}${query}`);
                          }}
                        >
                          <span className="sr-link-prefix">SR</span>
                          <span className="sr-link-code">{displaySrCode}</span>
                        </a>{" "}
                        from{" "}
                        <span style={{ color: getStatusColor(oldStatus), fontWeight: 700 }}>{oldStatus}</span> to{" "}
                        <span style={{ color: getStatusColor(newStatus), fontWeight: 700 }}>{newStatus}</span>.
                      </Typography>
                      {!!comments && (
                        <Typography className="timeline-subtext comments-line">
                          Comments: <span>{comments}</span>
                        </Typography>
                      )}
                      <Typography className="timeline-subtext prominent-meta user-meta-line">
                        User: <span className="meta-value">{userName}</span> | Employee:{" "}
                        {row.employeeCode ?? row.EmployeeCode ?? "-"} | Market:{" "}
                        <span className="meta-value">{marketName}</span> | Created:{" "}
                        {formatOnlyDate(row.userCreatedOn ?? row.UserCreatedOn ?? row.createdOn ?? row.CreatedOn)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {loadingMore && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {!loading && rows.length === 0 && <Box sx={{ p: 3, textAlign: "center" }}>No activity found.</Box>}
          {!!error && <Box sx={{ p: 2, color: "error.main" }}>{error}</Box>}
        </Box>
      </Paper>
    </Box>
  );
};

export default ServiceRequestActivityLog;
